// ============================================================
// THE WEAPON SPECIALS ON THE HIT (plan body-map H3; the bench's specials,
// ruled 2026-10-09). A weapon's special rules, merged into ONE moment per
// attack, each by its role:
//
//   blade    the shape of the cut (the first may move the figure)
//   motion   what the blow does to the whole figure (one per attack)
//   element  what burns, sparks or eats in every wound
//   accent   holy, psychic or warp light, after the last hit
//   after    the closing effects (morale, banes, drains), after that
//
// In a burst (./burst) the shape and the motion ride the first landed hit,
// an element lands in every wound, and the accents and closing effects
// follow the last. The looks are the plate's (components/plate/fx/specials).
//
// Pure. Zero React dependency. Seconds from the burst's start.
// ============================================================

import type { DamageCode } from '../impacts';
import { landed as landedOf, type BurstHit } from './burst';
import { parseWS } from '../weaponSpecials';

export type SpecialRole = 'blade' | 'motion' | 'element' | 'accent' | 'after';

/** Each role's specials, in the order they play (the bench's). */
const ROLES: Record<SpecialRole, readonly string[]> = {
  blade: ['cleaving', 'powerfield', 'razor', 'mono', 'ethereal', 'lance'],
  motion: ['impetus', 'graviton', 'concussive'],
  element: ['melta', 'shock', 'flame', 'corrosive', 'tearing', 'felling', 'toxicw', 'volatile'],
  accent: ['sanctified', 'blessed', 'force', 'warp', 'exorcist', 'witchbane'],
  after: [
    'pinning',
    'panicking',
    'cowing',
    'haywire',
    'snare',
    'armourbane',
    'breaching',
    'sunder',
    'crippling',
    'withering',
    'sapping',
    'numbing',
    'slowing',
    'devastating',
    'titanbane',
  ],
};

/** A weapon rule's name (engine/weaponSpecials) to its look's key, where the two differ. */
const RENAMED: Record<string, string> = {
  PowerField: 'powerfield',
  RazorSharp: 'razor',
  WarpWeapon: 'warp',
  Toxic: 'toxicw',
};

const ALL = new Set(Object.values(ROLES).flat());

/**
 * A rule's look (its rating dropped, its label read as its id - `Concussive(2)` and `Razor Sharp` alike, through the
 * rules' own parser), or null for a rule with none.
 */
export function specialKey(rule: string): string | null {
  const { id } = parseWS(rule.trim());
  const key = RENAMED[id] ?? id.toLowerCase();
  return ALL.has(key) ? key : null;
}

/** Whether a weapon's rules include this one (by the rules' own id: `Scrambler(20)` is Scrambler). */
export const hasSpecial = (rules: readonly string[], id: string) => rules.some((r) => parseWS(r.trim()).id === id);

/** A weapon's specials by role, each role in its playing order; one motion at most. */
export function specialsByRole(rules: readonly string[]): Record<SpecialRole, string[]> {
  const keys = new Set(rules.map(specialKey));
  const of = (role: SpecialRole) => ROLES[role].filter((k) => keys.has(k));
  return {
    blade: of('blade'),
    motion: of('motion').slice(0, 1),
    element: of('element'),
    accent: of('accent'),
    after: of('after'),
  };
}

/** One special's moment: where, when, and whether it may move the figure (`still`: it may not). */
export interface SpecialMoment {
  key: string;
  role: SpecialRole;
  location: string;
  at: number;
  still: boolean;
  /** The hit's damage type (the Power Field plays by it). */
  type: DamageCode;
}

/** When a shape or a motion leads, the elements wait for it; the accents and the closing effects follow in turn. */
const BEAT = { afterShape: 0.22, accent: 0.15, after: 0.35, each: 0.2 };

/** How long an element waits after its hit when a shape or a motion leads (Tearing's jolt finds its moment by it). */
export const SHAPE_LEAD = BEAT.afterShape;

/**
 * The attack's specials as moments over its burst's landed hits (a hit a field stopped takes none): the blades and
 * the motion on the first, an element in every wound (Shock on a blade crawls the edge instead), the accents and the
 * closing effects after the last.
 */
/** What a burst plays: its specials, then any looks a weapon rule lent it (plan armoury). */
export const playedRules = (burst: { specials: readonly string[]; looks?: readonly string[] }): readonly string[] =>
  burst.looks?.length ? [...burst.specials, ...burst.looks] : burst.specials;

export function specialMoments(rules: readonly string[], hits: readonly BurstHit[]): SpecialMoment[] {
  const landed = landedOf(hits);
  if (!landed.length) return [];
  const m = specialsByRole(rules);
  const bladed = m.blade.length > 0;
  const [first, last] = [landed[0], landed[landed.length - 1]];
  const shaped = bladed || m.motion.length > 0;
  const t = shaped ? BEAT.afterShape : 0;
  const at = (h: BurstHit, key: string, role: SpecialRole, when: number, still: boolean): SpecialMoment => ({
    key,
    role,
    location: h.location,
    at: when,
    still,
    type: h.type,
  });
  return [
    ...m.blade.map((k, i) => at(first, k, 'blade', first.at, m.motion.length > 0 || i > 0)),
    ...m.motion.map((k) => at(first, k, 'motion', first.at, false)),
    // An element in every wound - Shock on a blade crawls its edge, at the blow itself.
    ...m.element.flatMap((k) =>
      landed.map((h) =>
        k === 'shock' && bladed ? at(h, 'shockEdge', 'element', h.at, shaped) : at(h, k, 'element', h.at + t, shaped),
      ),
    ),
    ...m.accent.map((k, i) => at(last, k, 'accent', last.at + t + BEAT.accent + BEAT.each * i, true)),
    ...m.after.map((k, i) => at(last, k, 'after', last.at + t + BEAT.after + BEAT.each * i, true)),
  ];
}

/** The specials whose looks move the whole figure (the bench's `figMove`): a shove, a crush, a kick, a tremor. */
const MOVERS = new Set(['powerfield', 'impetus', 'graviton', 'concussive', 'shock']);

/** Whether a moment's look moves the figure: a mover's, but never the Power Field on Energy (sparks, not a shove). */
const moves = (s: SpecialMoment) => MOVERS.has(s.key) && !(s.key === 'powerfield' && s.type === 'E');

/**
 * The one move the attack makes of the whole figure, if any: the first moment free to move it (`still` false) whose
 * look moves at all, with its index among the moments (its look's seed). A closing effect never does - it plays after
 * another owned the move.
 */
export function figureMove(moments: readonly SpecialMoment[]): (SpecialMoment & { index: number }) | null {
  const index = moments.findIndex((s) => !s.still && moves(s));
  return index < 0 ? null : { ...moments[index], index };
}

/** The longest a look plays from its moment (Withering's 3.2 s glow, with its pieces' own delays). */
const LOOK_HOLD = 3.4;

/** Seconds until the last special's look is over (0 with none): the burst's play must last at least this long. */
export function specialsSpan(moments: readonly SpecialMoment[]): number {
  return moments.length ? Math.max(...moments.map((m) => m.at)) + LOOK_HOLD : 0;
}
